import { useEffect, useReducer } from 'react';
import Button from 'components/atoms/Button';
import Card from 'components/atoms/Card';
import Typography from 'components/atoms/Typography';
import HubAuthoringForm from 'pages/_AdminAuthoringPages/AdminHubAuthoring/HubAuthoringForm';
import { toast } from 'react-toastify';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import InAppRoutes, { pushProgressive } from 'utils/router';
import TabNav from 'components/molecules/TabNav';
import HubPreview from 'pages/_AdminAuthoringPages/_components/HubPreview';
import { useThunkDispatch } from 'utils/reduxHook';
import { createHub, getHub, updateHub } from 'actions/requests';
import { DiscoverItemType } from 'utils/gloo-types';
import DiscoverItemArchiveToggleButton from 'pages/_AdminAuthoringPages/_components/DiscoverItemArchiveToggleButton';
import classNames from 'classnames';
import { reducer, initialState, HubAuthoringContext, HubAuthoringDispatchContext } from './reducer';
import { ActionType } from './action';
import { HubAuthoringFormErrors, HubAuthoringTabLabel } from './types';
import { getFormErrors, sanitizeHubUpsertParams } from './utils';

const AdminHubCreate = () => {
  // Get redux state and dispatch
  const [state, dispatch] = useReducer(reducer, initialState);
  const globalDispatch = useThunkDispatch();
  const history = useHistory();
  const location = useLocation();

  const hubId = useParams<{ hubId: string }>().hubId;

  // Generate create or edit specific properties and labels
  const isEditHub = !!hubId;
  const navTitle = `${isEditHub ? 'Edit' : 'Create A'} Hub`;
  const dataDurableIdPrefix = `adminHub${isEditHub ? 'Edit' : 'Create'}`;
  const backButtonText = isEditHub ? 'Back' : 'Cancel';

  /**
   * General functions
   */
  const hydrateReduxStore = async () => {
    const hubResponse = await globalDispatch(getHub(hubId));
    dispatch({
      type: ActionType.hydrateStore,
      payload: hubResponse,
    });
    dispatch({
      type: ActionType.setIsLoading,
      isLoading: false,
    });
  };

  const onTabNavClick = () => {
    dispatch({
      type: ActionType.toggleIsEditMode,
    });
  };

  useEffect(() => {
    // Set hubId on redux store
    dispatch({
      type: ActionType.setHubId,
      hubId,
    });
  }, [hubId]);

  useEffect(() => {
    if (isEditHub) {
      dispatch({
        type: ActionType.setIsLoading,
        isLoading: true,
      });

      // query the hub and use it to hydrate the redux store
      hydrateReduxStore();
    }
  }, []);

  const onSave = async () => {
    // Get any form errors from the current state
    const formErrors: HubAuthoringFormErrors[] = getFormErrors(state);

    // If there are any errors then show the user a toast with instructions
    // and dispatch errors to state -- which will be rendered below the related
    // fields.
    if (formErrors.length > 0) {
      toast.error('Please fix the input fields highlighted in red.');
      // Dispatch errors to state
      dispatch({ type: ActionType.addErrors, payload: formErrors });
      return;
    }

    // Dispatch to remove any errors in state
    dispatch({ type: ActionType.removeErrors });

    const sanitizedPayload = sanitizeHubUpsertParams(state.hubUpsertParams);
    if (state.hubId) {
      const updatedHub = await globalDispatch(updateHub(state.hubId, sanitizedPayload));

      if (updatedHub?.id) {
        toast.success('Hub Updated');
      }
    } else {
      const newHub = await globalDispatch(createHub(sanitizedPayload));

      if (newHub?.id) {
        pushProgressive(InAppRoutes.AdminHubEdit.replace(':hubId', newHub.id));
        toast.success('Hub Created');
      }
    }
  };

  /**
   * Render functions
   */
  return (
    <div className="mx-[54px] my-[32px]">
      <Card className="flex items-center justify-between p-4 mb-6">
        <Button
          dataDurableId={`${dataDurableIdPrefix}BackButton`}
          type="secondary"
          icon="arrow-left"
          onClick={() => {
            if (!location.key) {
              pushProgressive(InAppRoutes.AdminAuthoringHome);
            } else {
              history.goBack();
            }
          }}
          text={backButtonText}
          className={classNames('mr-4')}
        />
        <Typography className="text-thryveGreenDark flex-grow" variant="h1">
          {navTitle}
        </Typography>
        <div className="flex flex-row gap-4">
          {isEditHub && hubId && (
            <DiscoverItemArchiveToggleButton
              archivedAt={state.archivedAt}
              descriptor={{
                type: DiscoverItemType.HUB,
                id: hubId,
              }}
              onArchivedAtChange={(archivedAt) => {
                dispatch({
                  type: ActionType.setArchivedAt,
                  archivedAt,
                });
              }}
            />
          )}
          <Button
            icon="save"
            iconPosition="left"
            buttonType="submit"
            dataDurableId={`${dataDurableIdPrefix}SaveButton`}
            type="primary"
            text="SAVE"
            onClick={onSave}
            disabled={!!hubId && !!state.archivedAt}
          />
        </div>
      </Card>
      <nav>
        <div className="bg-white inline-block rounded-sm mb-6">
          <TabNav
            itemClassName="mx-0 my-0 py-3 px-7"
            onTabClick={onTabNavClick}
            tabs={[HubAuthoringTabLabel.Edit, HubAuthoringTabLabel.Preview]}
          />
        </div>
      </nav>
      <HubAuthoringContext.Provider value={state}>
        <HubAuthoringDispatchContext.Provider value={dispatch}>
          {!state.isLoading && state.isEditMode && <HubAuthoringForm />}
          {!state.isLoading && !state.isEditMode && <HubPreview hubUpsertParams={state.hubUpsertParams} />}
        </HubAuthoringDispatchContext.Provider>
      </HubAuthoringContext.Provider>
    </div>
  );
};

export default AdminHubCreate;
